import { useEffect, useState } from 'react';
import { clientFetch, navigate } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  card: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    color: '#FFF',
  },
  cardTitle: {
    fontSize: '14px',
    color: '#9CA3AF',
    marginBottom: '8px',
    fontWeight: '600',
  },
  cardValue: {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '8px',
  },
  cardSubtext: {
    fontSize: '13px',
    color: '#9CA3AF',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#FFF',
    marginBottom: '16px',
    marginTop: '32px',
  },
  notificationItem: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  notificationTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  notificationMessage: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '8px',
  },
  notificationTime: {
    fontSize: '12px',
    color: '#6B7280',
  },
  managerCard: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
  },
  managerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  managerTitle: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '12px',
  },
  contactInfo: {
    fontSize: '13px',
    color: '#3B82F6',
    marginBottom: '4px',
    cursor: 'pointer',
  },
  accentBadge: {
    backgroundColor: '#3B82F6',
    color: '#FFF',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
};

export default function ClientDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/dashboard');
        setDashboardData(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <ClientShell title="Dashboard">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading dashboard...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Dashboard">
        <div style={styles.container}>
          <p style={{ color: '#EF4444' }}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  const {
    activeServices = 0,
    openTickets = 0,
    unpaidInvoices = 0,
    onboardingProgress = 0,
    upcomingExpiries = [],
    recentNotifications = [],
    accountManager = null,
  } = dashboardData || {};

  return (
    <ClientShell title="Dashboard">
      <div style={styles.container}>
        {/* Summary Cards */}
        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Active Services</div>
            <div style={styles.cardValue}>{activeServices}</div>
            <div style={styles.cardSubtext}>Currently active</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Open Tickets</div>
            <div style={styles.cardValue}>{openTickets}</div>
            <div style={styles.cardSubtext}>Need attention</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Unpaid Invoices</div>
            <div style={styles.cardValue}>{unpaidInvoices}</div>
            <div style={styles.cardSubtext}>Outstanding balance</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Onboarding Progress</div>
            <div style={styles.cardValue}>{onboardingProgress}%</div>
            <div style={styles.cardSubtext}>Steps completed</div>
          </div>
        </div>

        {/* Upcoming Contract Expiries */}
        <div>
          <div style={styles.sectionTitle}>Upcoming Contract Expiries</div>
          {upcomingExpiries && upcomingExpiries.length > 0 ? (
            <div style={styles.grid}>
              {upcomingExpiries.map((contract) => (
                <div key={contract.id} style={styles.card}>
                  <div style={styles.cardTitle}>{contract.title}</div>
                  <div style={styles.cardValue}>{contract.daysRemaining}</div>
                  <div style={styles.cardSubtext}>days remaining</div>
                  <div style={styles.cardSubtext}>Expires: {contract.endDate}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.cardSubtext}>No upcoming expiries</p>
          )}
        </div>

        {/* Recent Notifications */}
        <div>
          <div style={styles.sectionTitle}>Recent Notifications</div>
          {recentNotifications && recentNotifications.length > 0 ? (
            recentNotifications.map((notification) => (
              <div key={notification.id} style={styles.notificationItem}>
                <div style={styles.notificationTitle}>{notification.title}</div>
                <div style={styles.notificationMessage}>{notification.message}</div>
                <div style={styles.notificationTime}>{notification.time}</div>
              </div>
            ))
          ) : (
            <p style={styles.cardSubtext}>No recent notifications</p>
          )}
        </div>

        {/* Account Manager Info */}
        {accountManager && (
          <div>
            <div style={styles.sectionTitle}>Your Account Manager</div>
            <div style={styles.managerCard}>
              <div style={styles.managerName}>{accountManager.name}</div>
              <div style={styles.managerTitle}>{accountManager.title}</div>
              <div style={styles.contactInfo}>
                <a href={`mailto:${accountManager.email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                  {accountManager.email}
                </a>
              </div>
              <div style={styles.contactInfo}>
                <a href={`tel:${accountManager.phone}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                  {accountManager.phone}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
